import { Badge } from '@/components/ui/badge';
import { CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface MainStep {
  id: string;
  step_number: number;
  title: string;
  isCompleted: boolean;
  isCurrent: boolean;
}

interface WorkflowProgressBarProps {
  mainSteps: MainStep[];
  currentMainStepIndex: number;
  onStepClick?: (index: number) => void;
}

/**
 * Displays main steps progress at the top of the workflow
 * Shows all steps and allows clicking to navigate between them
 * Steps can be completed in any order (parallel workflow)
 */
export const WorkflowProgressBar = ({ mainSteps, currentMainStepIndex, onStepClick }: WorkflowProgressBarProps) => {
  return (
    <div className="flex items-center justify-center gap-2 py-4 px-4 overflow-x-auto">
      {mainSteps.map((step, index) => {
        const isCurrent = index === currentMainStepIndex;

        return (
          <div
            key={step.id}
            className={cn(
              'flex items-center gap-2 transition-all duration-300',
              isCurrent && 'scale-110',
              !isCurrent && 'scale-95'
            )}
          >
            <button
              onClick={() => onStepClick?.(index)}
              className="flex flex-col items-center gap-1 min-w-[100px] group cursor-pointer hover:scale-105 transition-transform"
              type="button"
            >
              <div
                className={cn(
                  'w-12 h-12 rounded-full flex items-center justify-center font-bold transition-all',
                  isCurrent && 'bg-primary text-primary-foreground ring-4 ring-primary/20',
                  !isCurrent && step.isCompleted && 'bg-success text-success-foreground group-hover:ring-2 group-hover:ring-success/30',
                  !isCurrent && !step.isCompleted && 'bg-muted text-muted-foreground border-2 border-border group-hover:border-primary group-hover:text-foreground'
                )}
              >
                {step.isCompleted ? (
                  <CheckCircle2 className="h-6 w-6" />
                ) : (
                  <span>{step.step_number}</span>
                )}
              </div>
              <span
                className={cn(
                  'text-xs text-center font-medium truncate max-w-[100px]',
                  isCurrent && 'text-foreground font-semibold',
                  !isCurrent && 'text-muted-foreground group-hover:text-foreground'
                )}
                title={step.title}
              >
                {step.title}
              </span>
            </button>
            {index < mainSteps.length - 1 && (
              <div
                className={cn(
                  'h-0.5 w-8 transition-all',
                  'bg-border'
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
